/**
 * useQueryList
 * Fetches the list of queries available to the user and exposes create/update/
 * delete mutations that invalidate the list on success.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryService } from '@services';
import { showToast } from '@utils/toast';
import type { CreateQueryRequest, UpdateQueryRequest, ListQueriesParams } from '@app-types';

const EMPTY_COUNTS = { all: 0, personal: 0, global: 0 } as const;

export const useQueryList = (params: ListQueriesParams = {}, enabled = true) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    // Re-fetch whenever any server-side list parameter changes (US-02).
    queryKey: ['queries', params],
    queryFn: () => queryService.list(params),
    // The app-wide defaults (staleTime 5m, refetchOnMount false) would serve a
    // stale list when returning to /queries right after a create/update/delete:
    // invalidateQueries marks the inactive list stale but, with refetchOnMount
    // off, the remount won't refetch until a param (tab) change forces a new key
    // (#864). Always refetch fresh on mount so counts and rows reflect the change.
    staleTime: 0,
    refetchOnMount: 'always',
    enabled
  });

  // Invalidate every list page/filter variant after a mutation.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['queries'] });

  // Success notifications (US-03, US-06, US-07).
  const createMutation = useMutation({
    mutationFn: (payload: CreateQueryRequest) => queryService.create(payload),
    onSuccess: () => {
      invalidate();
      showToast.success('Query created successfully');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateQueryRequest }) => queryService.update(id, payload),
    onSuccess: (_, { id }) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['query', id] });
      queryClient.removeQueries({ queryKey: ['queryExec', id] });
      showToast.success('Changes saved successfully');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => queryService.remove(id),
    onSuccess: () => {
      invalidate();
      showToast.success('Query deleted successfully');
    }
  });

  return {
    queries: data?.data ?? [],
    total: data?.count ?? 0,
    counts: data?.counts ?? EMPTY_COUNTS,
    page: data?.page ?? (params.page ?? 1),
    pageSize: data?.pageSize ?? (params.pageSize ?? 0),
    isLoading,
    error,
    createQuery: createMutation.mutateAsync,
    updateQuery: updateMutation.mutateAsync,
    deleteQuery: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
};
